import { useEffect } from 'react';
import { createSearchParams, useNavigate } from 'react-router';
import { WizardStepName } from '../../../data/ppoints/constants';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { wizardActions, wizardMarkDoneThunk } from '../../../redux/slices/wizard-slice';

type UseWizardOptions = {
    autoOpenFirstUndoneStep?: boolean;
};

const useWizard = (options: UseWizardOptions = {}) => {
    const dispatch = useAppDispatch();
    const settingsState = useAppSelector((state) => state.settings);
    const wizardState = useAppSelector((state) => state.wizard);
    const navigate = useNavigate();

    const setOpenStepName = (stepName: WizardStepName | null) => {
        if (!stepName) return;
        dispatch(wizardActions.setSelectedStepName(stepName));
    };

    const isStepDone = (stepName: WizardStepName) => {
        if (!wizardState.steps) {
            return false;
        }
        let step = wizardState.steps.find((step) => step.name === stepName);
        if (!step) return false;
        return step.done;
    };

    const markStepDone = async (stepName: WizardStepName) => {
        if (isStepDone(stepName)) return;
        await dispatch(wizardMarkDoneThunk({ stepName: stepName }));
    };

    const doneAllSteps = () => {
        const summaryStepUndone = wizardState.steps.filter((step) => !step.done);
        summaryStepUndone.forEach((step) => {
            dispatch(wizardMarkDoneThunk({ stepName: step.name, skip: true }));
        });
    };

    const navigateIfStepNotDone = (
        stepName: WizardStepName,
        url: string,
        queryParams: Record<string, string> | null = null
    ) => {
        if (!isStepDone(stepName)) {
            if (queryParams) {
                navigate({
                    pathname: url,
                    search: createSearchParams(queryParams).toString(),
                });
            } else {
                navigate(url);
            }
        }
    };

    const countCompletedSteps = () => {
        if (!wizardState.steps) return 0;

        // Otherwise return count of completed steps
        return wizardState.steps.filter((step) => step.done).length;
    };

    const isAllDone = () => {
        return countCompletedSteps() / (wizardState.steps?.length || 0) == 1;
    };
    const getFirstUndoneStepName = () => {
        if (isAllDone()) return null;
        return wizardState.steps?.find((step) => !step.done)?.name || null;
    };

    /**
     * A step is locked until every step before it is done,
     * so the merchant has to go through the wizard step-by-step.
     */
    const isStepLocked = (stepName: WizardStepName) => {
        if (!wizardState.steps) return false;
        const stepIndex = wizardState.steps.findIndex((step) => step.name === stepName);
        if (stepIndex <= 0) return false;
        return wizardState.steps.slice(0, stepIndex).some((step) => !step.done);
    };

    const handleStepToggle = (stepName: WizardStepName) => {
        if (isStepLocked(stepName)) return;
        setOpenStepName(stepName);
    };

    const handleWizardStepper = async (
        stepName: WizardStepName,
        nextStep?: { stepName: WizardStepName; url: string; queryParams?: Record<string, string> }
    ) => {
        if (wizardState.hasUndoneSteps) {
            if (!isStepDone(stepName)) {
                await markStepDone(stepName);
            }
            if (nextStep) {
                navigateIfStepNotDone(nextStep.stepName, nextStep.url, nextStep.queryParams);
            }
        }
    };

    useEffect(() => {
        if (!options.autoOpenFirstUndoneStep) {
            return;
        }
        if (!wizardState.steps || !wizardState.initialized) {
            return;
        }
        setOpenStepName(getFirstUndoneStepName());
    }, [options.autoOpenFirstUndoneStep, wizardState.steps, wizardState.initialized]);

    /**
     * Never keep a locked step open: fall back to the first step the merchant can actually do.
     */
    const openStepName =
        wizardState.selectedStepName && !isStepLocked(wizardState.selectedStepName)
            ? wizardState.selectedStepName
            : getFirstUndoneStepName();

    return {
        settingsState,
        wizardState,
        openStepName,
        handleStepToggle,
        countCompletedSteps,
        isAllDone,
        markStepDone,
        isStepDone,
        isStepLocked,
        handleWizardStepper,
        doneAllSteps,
    };
};

export default useWizard;
